import React from 'react';
import { buildForecastDays, degreesToDirection, formatForecastDate, getWeatherIconPath, getWeatherLabel, getWindCategory, getWindCategoryLabel } from './utils';

const labels = {
  english: {
    title: 'Current conditions',
    temperature: 'Air temperature',
    feelsLike: 'Feels like',
    windSpeed: 'Wind speed',
    windDirection: 'Wind direction',
    windType: 'Wind type',
    seaTemperature: 'Sea temperature',
    weatherType: 'Weather',
    unavailable: 'Weather data not available',
    nextTenDays: '10-day outlook',
    dayHigh: 'High',
    dayLow: 'Low',
    maxWind: 'Wind',
    directionFrom: 'from',
    selected: 'Selected day',
  },
  swedish: {
    title: 'Nuvarande förhållanden',
    temperature: 'Lufttemperatur',
    feelsLike: 'Känns som',
    windSpeed: 'Vindhastighet',
    windDirection: 'Vindriktning',
    windType: 'Vindtyp',
    seaTemperature: 'Havstemperatur',
    weatherType: 'Väder',
    unavailable: 'Väderdata är inte tillgänglig',
    nextTenDays: '10-dygnsöversikt',
    dayHigh: 'Max',
    dayLow: 'Min',
    maxWind: 'Vind',
    directionFrom: 'från',
    selected: 'Vald dag',
  },
};

const WeatherCard = ({ weatherData, seaTemperature, language, selectedDate, onSelectDate }) => {
  const text = labels[language] || labels.english;
  const current = weatherData?.current;
  const windCategory = current ? getWindCategory(current.wind_direction_10m) : null;
  const forecastDays = buildForecastDays(weatherData);
  const currentIcon = getWeatherIconPath(current?.weather_code, Boolean(current?.is_day));
  const currentWeatherLabel = current ? getWeatherLabel(current.weather_code, language) : text.unavailable;

  const rows = [
    [text.weatherType, currentWeatherLabel, null],
    [text.temperature, current ? `${current.temperature_2m}°C` : text.unavailable, 'air-temp-icon.png'],
    [text.feelsLike, current ? `${current.apparent_temperature}°C` : text.unavailable, 'air-temp-icon.png'],
    [text.windSpeed, current ? `${current.wind_speed_10m} m/s` : text.unavailable, 'wind-icon.png'],
    [text.windDirection, current ? `${degreesToDirection(current.wind_direction_10m)} · ${Math.round(current.wind_direction_10m)}°` : text.unavailable, 'compass-icon.png'],
    [text.windType, current ? getWindCategoryLabel(windCategory, language) : text.unavailable, 'wind-icon.png'],
    [text.seaTemperature, seaTemperature !== null ? `${seaTemperature}°C` : text.unavailable, 'water-temp-icon.png'],
  ];

  return (
    <section className="panel panel-weather">
      <div className="panel-heading panel-heading--weather">
        <div>
          <p className="eyebrow">{language === 'english' ? 'Weather' : 'Väder'}</p>
          <h2>{text.title}</h2>
        </div>
        <div className="weather-summary-badge">
          <img className="weather-summary-badge__icon icon-md" src={currentIcon} alt={currentWeatherLabel} />
          <span>{currentWeatherLabel}</span>
        </div>
      </div>

      <div className="stats-list">
        {rows.map(([label, value, icon]) => (
          <div className="stat-row" key={label}>
            <span>
              {icon && <img className="stat-row__icon icon-sm" src={`${process.env.PUBLIC_URL}/static/icons/general/${icon}`} alt="" />}
              {label}
            </span>
            <strong>{value}</strong>
          </div>
        ))}
      </div>

      <div className="forecast-block">
        <div className="forecast-block__header">
          <p className="eyebrow">{language === 'english' ? 'Forecast' : 'Prognos'}</p>
          <h3>{text.nextTenDays}</h3>
        </div>

        <div className="forecast-grid">
          {forecastDays.map((day) => {
            const category = getWindCategory(day.windDirectionDominant ?? 0);
            const isSelected = selectedDate === day.time;

            return (
              <button
                type="button"
                className={isSelected ? 'forecast-day forecast-day--selected' : 'forecast-day'}
                key={day.time}
                onClick={() => onSelectDate(day.time)}
                aria-pressed={isSelected}
              >
                <div className="forecast-day__header">
                  <div className="forecast-day__title">
                    <img
                      className="forecast-day__weather-icon icon-md"
                      src={getWeatherIconPath(day.weatherCode, true)}
                      alt={getWeatherLabel(day.weatherCode, language)}
                    />
                    <div>
                      <strong>{formatForecastDate(day.time, language)}</strong>
                      <p className="forecast-day__weather-label">{getWeatherLabel(day.weatherCode, language)}</p>
                    </div>
                  </div>
                  <span className={`forecast-wind-badge forecast-wind-badge--${category}`}>
                    {getWindCategoryLabel(category, language)}
                  </span>
                </div>

                <div className="forecast-day__metrics">
                  <div>
                    <span>{text.dayHigh}</span>
                    <strong>{day.temperatureMax != null ? `${Math.round(day.temperatureMax)}°C` : text.unavailable}</strong>
                  </div>
                  <div>
                    <span>{text.dayLow}</span>
                    <strong>{day.temperatureMin != null ? `${Math.round(day.temperatureMin)}°C` : text.unavailable}</strong>
                  </div>
                  <div>
                    <span>{text.maxWind}</span>
                    <strong>{day.windSpeedMax != null ? `${Math.round(day.windSpeedMax)} m/s` : text.unavailable}</strong>
                  </div>
                </div>

                <p className="forecast-day__direction">
                  {degreesToDirection(day.windDirectionDominant ?? 0)} {text.directionFrom} {Math.round(day.windDirectionDominant ?? 0)}°
                </p>

                {isSelected ? <span className="forecast-day__selected-label">{text.selected}</span> : null}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WeatherCard;
